import ReplayIcon from '@mui/icons-material/Replay'
import { IconButton, Tooltip } from '@mui/material'
import { isEmpty } from 'lodash'
import { useCallback, useState } from 'react'
import { required, SelectInput, TextInput, useTranslate } from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'

import { ACCOUNT_FREE_BILLING_TYPE_REQUIRES_JUSTIFICATION_SOURCE } from '../config/accounts'
import {
  BOOKING_BILLING_SETTINGS_SOURCE,
  BOOKING_BILLING_TYPE_AUTO,
  BOOKING_BILLING_TYPE_FREE,
  BOOKING_BILLING_TYPE_PRO,
  BOOKING_BILLING_TYPE_SOURCE,
  BOOKING_MAKE_BOOKING_FREE_SOURCE,
} from '../config/bookings'

const OPTION_CODE_OTHER = 'OTHER'

const BookingJustificationInput = ({ bookingsJustifications, source }) => {
  const [justification, billingType, billingSettings, makeBookingFree] = useWatch({
    name: [source, BOOKING_BILLING_TYPE_SOURCE, BOOKING_BILLING_SETTINGS_SOURCE, BOOKING_MAKE_BOOKING_FREE_SOURCE],
  })
  const translate = useTranslate()
  const { setValue } = useFormContext()

  const hasBookingsJustifications = !isEmpty(bookingsJustifications)
  const [optionCode, setOptionCode] = useState(
    Boolean(justification) && hasBookingsJustifications
      ? (bookingsJustifications.find((j) => j.code === justification)?.code ?? OPTION_CODE_OTHER)
      : null,
  )

  const onSelectInputChange = useCallback(
    ({ target: { value } }) => {
      setOptionCode(value)
      if (value === OPTION_CODE_OTHER) {
        setValue(source, null)
      }
    },
    [setValue, source],
  )

  const resetInput = useCallback(() => {
    setValue(source, null)
    setOptionCode(null)
  }, [setValue, source])

  const isBillingTypeFree = makeBookingFree || billingType === BOOKING_BILLING_TYPE_FREE
  const shouldDisplayJustificationInput =
    [BOOKING_BILLING_TYPE_PRO, BOOKING_BILLING_TYPE_AUTO].includes(billingType) ||
    (isBillingTypeFree && billingSettings[ACCOUNT_FREE_BILLING_TYPE_REQUIRES_JUSTIFICATION_SOURCE])

  if (!shouldDisplayJustificationInput || !Boolean(billingSettings)) {
    return null
  }

  const requiredErrorMessage = isBillingTypeFree
    ? 'resources.bookings.forms.validation.justification.requiredForFreeBookings'
    : 'resources.bookings.forms.validation.justification.requiredForProAutoBookings'
  const commonProps = { source, validate: required(requiredErrorMessage) }

  const bookingsJustificationsChoices = bookingsJustifications
    .sort((a, b) => (a.code === OPTION_CODE_OTHER) - (b.code === OPTION_CODE_OTHER)) // put 'OTHER' option at the end
    .map(({ code, label }) => ({ id: code, name: label }))

  return hasBookingsJustifications && optionCode !== OPTION_CODE_OTHER ? (
    <SelectInput
      {...commonProps}
      choices={bookingsJustificationsChoices}
      onChange={onSelectInputChange}
      value={optionCode}
    />
  ) : (
    <>
      <TextInput {...commonProps} />
      {hasBookingsJustifications && optionCode === OPTION_CODE_OTHER && (
        <Tooltip title={translate('mymove.goBackToList')}>
          <IconButton sx={{ width: 26, height: 26, m: '15px -8px 0 6px' }} onClick={resetInput}>
            <ReplayIcon sx={{ width: 20, height: 20 }} />
          </IconButton>
        </Tooltip>
      )}
    </>
  )
}

export default BookingJustificationInput
